/**
 * Density tables for rendered markdown.
 *
 * These live in their own module because two layers need them and neither
 * should own them: the component maps (`chat-components`, `prose-components`)
 * that hand element types to react-markdown, and the block chrome
 * (`code-blocks`, `MermaidBlock`) those maps render into. Keeping one table
 * here is what stops a mermaid fence and the code fence next to it from
 * drifting apart on the next spacing change.
 */

/** Density of a rendered markdown tree. Chat surfaces use `sm`/`xs`; docs use `prose`. */
export type MarkdownSize = 'sm' | 'xs' | 'prose';

/** The two chat densities. Prose chrome is fixed and lives in `CodeBlockShell`. */
export type ChatSize = Exclude<MarkdownSize, 'prose'>;

export interface ChatSizeConfig {
  h1: string;
  h2: string;
  h3: string;
  h4: string;
  body: string;
  blockCode: string;
  inlineCode: string;
  table: string;
  td: string;
  spacing: { heading: string; body: string };
  container: { headerPad: string; codePad: string; tablePad: string };
}

export const CHAT_SIZE_CLASSES: Record<ChatSize, ChatSizeConfig> = {
  sm: {
    h1: 'text-lg',
    h2: 'text-base',
    h3: 'text-sm',
    h4: 'text-sm',
    body: 'text-sm',
    blockCode: 'text-sm',
    inlineCode: 'text-sm',
    table: 'text-sm',
    td: 'text-sm',
    spacing: { heading: 'mt-4 mb-2', body: 'mb-2' },
    container: { headerPad: 'px-3 py-1.5', codePad: 'p-3', tablePad: 'px-3 py-1.5' },
  },
  xs: {
    h1: 'text-sm',
    h2: 'text-xs',
    h3: 'text-xs',
    h4: 'text-xs',
    body: 'text-xs',
    blockCode: 'text-xs',
    inlineCode: 'text-xs',
    table: 'text-xs',
    td: 'text-xs',
    spacing: { heading: 'mt-2 mb-1.5', body: 'mb-1.5' },
    container: { headerPad: 'px-2 py-0.5', codePad: 'px-2 py-1.5', tablePad: 'px-2 py-1' },
  },
};
